if (!customElements.get('card-product')) {
  customElements.define(
    'card-product',
    class CardProduct extends HTMLElement {
      constructor() {
        super();

        this._selectors = {
          swatchLinksWrapper: '[js-swatch-links-wrapper]',
          swatchLink: '[js-swatch-link]',
          mainImage: '.card__media .media img:first-of-type',
          secondaryImage: '.card__media .media img:nth-of-type(2)',
          productTitleLinks: '.card__heading a',
          cardWrapper: 'card-product'
        };
      }

      connectedCallback() {
        this.currentColor = this.dataset.currentColor;
        this.swatchLinksWrappers = this.querySelectorAll(this._selectors.swatchLinksWrapper);

        if (this.classList.contains('card-product--hover-quick-add')) return;

        if (this.swatchLinksWrappers.length > 0) {
          this._initSwatchLinks();
        }
      }

      _swatchButtonOnClick = (evt) => {
        evt.preventDefault();
        const button = evt.currentTarget;
        // Decode HTML entities before parsing JSON (reverse order of encoding)
        const decodedData = button.dataset.productData
          .replace(/&amp;/g, '&')
          .replace(/&#39;/g, "'")
          .replace(/&quot;/g, '"')
          .replace(/&gt;/g, '>')
          .replace(/&lt;/g, '<');
        const productData = JSON.parse(decodedData);
        
        this._updateProductCard(productData);
      }

      _generateImageSrcset = (imageUrl) => {
        if (!imageUrl) return '';
        
        // Handle Shopify image URLs - they can be in format:
        // https://cdn.shopify.com/.../image.jpg or
        // https://cdn.shopify.com/.../image_master.jpg or
        // https://cdn.shopify.com/.../image_100x100.jpg
        
        // Remove existing size parameters and query strings
        let baseUrl = imageUrl.split('?')[0]; // Remove query string
        baseUrl = baseUrl.replace(/_(master|\d+x\d+|\d+x)\.(jpg|jpeg|png|webp|gif)$/i, '.$2'); // Remove size suffix
        
        // Extract extension
        const extension = baseUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[1] || 'jpg';
        const queryString = imageUrl.includes('?') ? '?' + imageUrl.split('?')[1] : '';
        
        // Generate srcset with Shopify size parameters
        const sizes = [165, 360, 533, 720, 940, 1066];
        const srcsetParts = sizes.map(size => {
          return `${baseUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '')}_${size}x.${extension}${queryString} ${size}w`;
        });
        
        // Add master size
        srcsetParts.push(`${baseUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '')}_master.${extension}${queryString} master`);
        
        return srcsetParts.join(', ');
      }

      _updateProductCard = (productData) => {
        // Update main image
        const mainImage = this.querySelector(this._selectors.mainImage);
        if (mainImage && productData.imageUrl) {
          const srcset = this._generateImageSrcset(productData.imageUrl);
          if (srcset) {
            mainImage.srcset = srcset;
            // Set src to 533px version for fallback
            const baseUrl = productData.imageUrl.split('?')[0].replace(/_(master|\d+x\d+|\d+x)\.(jpg|jpeg|png|webp|gif)$/i, '.$2');
            const extension = baseUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[1] || 'jpg';
            const queryString = productData.imageUrl.includes('?') ? '?' + productData.imageUrl.split('?')[1] : '';
            mainImage.src = `${baseUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '')}_533x.${extension}${queryString}`;
          }
        }

        // Update secondary image (hover image)
        const secondaryImage = this.querySelector(this._selectors.secondaryImage);
        if (secondaryImage && productData.alternateImageUrls && productData.alternateImageUrls.length > 0) {
          const hoverImageUrl = productData.alternateImageUrls[0];
          const srcset = this._generateImageSrcset(hoverImageUrl);
          if (srcset) {
            secondaryImage.srcset = srcset;
            // Set src to 533px version for fallback
            const baseUrl = hoverImageUrl.split('?')[0].replace(/_(master|\d+x\d+|\d+x)\.(jpg|jpeg|png|webp|gif)$/i, '.$2');
            const extension = baseUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i)?.[1] || 'jpg';
            const queryString = hoverImageUrl.includes('?') ? '?' + hoverImageUrl.split('?')[1] : '';
            secondaryImage.src = `${baseUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '')}_533x.${extension}${queryString}`;
          }
        }

        // Update product title links
        const titleLinks = this.querySelectorAll(this._selectors.productTitleLinks);
        titleLinks.forEach(link => {
          link.href = productData.url;
          link.textContent = productData.name;
        });

        // Update card data attributes
        this.dataset.handle = productData.handle;
        this.dataset.currentColor = productData.colorName;

        // Update active swatch button state
        this.swatchLinks.forEach(btn => {
          btn.classList.remove('active');
          if (btn.dataset.value === productData.colorName) {
            btn.classList.add('active');
          }
        });
      }

      _handleSwatchLinks = () => {
        this.swatchLinks = this.querySelectorAll(this._selectors.swatchLink);

        if (this.swatchLinks.length < 1) {
          return;
        }

        this.swatchLinks.forEach((swatchButton) => {
          swatchButton.addEventListener('click', this._swatchButtonOnClick);
          
          // Mark current color as active
          if (swatchButton.dataset.value === this.currentColor) {
            swatchButton.classList.add('active');
          }
        });
      }

      _getRelatedProductsJSON = (url) => {
        const results = fetch(url)
          .then(response => response.text())
          .then((text) => {
            const html = text;
            const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
            const JSONcontainer = parsedHTML.querySelector('[js-products-json]');
            const parsedJSON = JSON.parse(JSONcontainer.innerHTML);
            return parsedJSON;
          });
        return results;
      }

      _initSwatchLinks = () => {
        const init = async () => {
          const tag = this.swatchLinksWrappers[0].dataset.tag;
          const targetURL = `${window.Shopify.routes.root}collections/all/${tag}?view=json`;
          const products = await this._getRelatedProductsJSON(targetURL);   
          this.swatchLinksWrappers.forEach((container) => {
            let swatchLinksMarkup = '';

            products.forEach((product) => {
              // Only include the data we actually need for updating the card
              const minimalProductData = {
                imageUrl: product.imageUrl,
                alternateImageUrls: product.alternateImageUrls,
                url: product.url,
                name: product.name,
                handle: product.handle,
                colorName: product.colorName
              };
              
              // HTML-encode JSON for safe use in HTML attribute
              const productDataJson = JSON.stringify(minimalProductData);
              // Escape HTML special characters for use in attribute (order matters - & must be last)
              const productData = productDataJson
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;')
                .replace(/&/g, '&amp;');
              swatchLinksMarkup = `${swatchLinksMarkup}
                                <button type="button" class="product__swatch-link" id="${product.colorName}" aria-labelledby="${product.colorName}" data-value="${product.colorName}" data-product-data='${productData}' style="background-color: ${!product.colorImg.includes('no-image') ? '' : product.colorHex}; background-image: url(${product.colorImg.includes('no-image') ? '' : product.colorImg}); background-size: cover;" js-swatch-link></button>`;
            });

            container.innerHTML = swatchLinksMarkup;
            container.dataset.loaded = 'true';
          });

          this._handleSwatchLinks();
        }

        init();
      }
    }
  );
}

